import React, { useEffect, useState } from "react";

const BUILDING_STATES = [
  { icon: "⚙️", headline: "Spinning up your app", sub: "Warming up the engine..." },
  { icon: "📦", headline: "Bundling modules", sub: "Packing everything neatly..." },
  { icon: "🔌", headline: "Connecting services", sub: "Linking all the pieces..." },
  { icon: "🧪", headline: "Running health checks", sub: "Making sure nothing's broken..." },
  { icon: "🚀", headline: "Almost there", sub: "Final touches in progress..." },
  { icon: "🛠️", headline: "Patching things up", sub: "Fixing a few loose ends..." },
  { icon: "🌐", headline: "Deploying to preview", sub: "Getting your app online..." },
  { icon: "✨", headline: "Polishing the UI", sub: "Making it look just right..." },
];

export default function BuildingScreen() {
  const [stateIndex, setStateIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [dots, setDots] = useState("");

  useEffect(() => {
    const dotInterval = setInterval(() => {
      setDots((d) => (d.length >= 3 ? "" : d + "."));
    }, 400);
    return () => clearInterval(dotInterval);
  }, []);

  useEffect(() => {
    const cycle = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setStateIndex((i) => (i + 1) % BUILDING_STATES.length);
        setVisible(true);
      }, 400);
    }, 8000);
    return () => clearInterval(cycle);
  }, []);

  const current = BUILDING_STATES[stateIndex];

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        background: "#fafafa",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Inter', sans-serif",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
          linear-gradient(rgba(0,0,0,0.04) 1px, transparent 1px),
          linear-gradient(90deg, rgba(0,0,0,0.04) 1px, transparent 1px)
        `,
          backgroundSize: "32px 32px",
          maskImage:
            "radial-gradient(ellipse 70% 70% at 50% 50%, black 30%, transparent 100%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 70% 70% at 50% 50%, black 30%, transparent 100%)",
        }}
      />

      <div
        style={{
          position: "absolute",
          width: 480,
          height: 480,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(99,102,241,0.07) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          position: "relative",
          width: 100,
          height: 100,
          marginBottom: 32,
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            border: "1.5px solid rgba(99,102,241,0.12)",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            border: "2px solid transparent",
            borderTopColor: "#6366f1",
            borderRightColor: "rgba(99,102,241,0.3)",
            animation: "spin 1.2s linear infinite",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 10,
            borderRadius: "50%",
            border: "1.5px solid transparent",
            borderBottomColor: "#8b5cf6",
            borderLeftColor: "rgba(139,92,246,0.3)",
            animation: "spin 1.8s linear infinite reverse",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 28,
            transition: "opacity 0.4s ease",
            opacity: visible ? 1 : 0,
          }}
        >
          {current.icon}
        </div>
      </div>

      <div
        style={{
          textAlign: "center",
          transition: "opacity 0.4s ease, transform 0.4s ease",
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(6px)",
        }}
      >
        <h2
          style={{
            margin: "0 0 8px",
            fontSize: 18,
            fontWeight: 600,
            color: "#18181b",
            letterSpacing: "-0.02em",
          }}
        >
          {current.headline}
          {dots}
        </h2>
        <p
          style={{
            margin: 0,
            fontSize: 13,
            color: "#71717a",
            fontWeight: 400,
          }}
        >
          {current.sub}
        </p>
      </div>

      <div
        style={{
          marginTop: 36,
          width: 200,
          height: 3,
          borderRadius: 99,
          background: "rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            background: "linear-gradient(90deg, #6366f1, #8b5cf6)",
            borderRadius: 99,
            animation: "indeterminate 1.6s ease-in-out infinite",
            transformOrigin: "left center",
          }}
        />
      </div>

      <div
        style={{
          display: "flex",
          gap: 6,
          marginTop: 24,
          flexWrap: "wrap",
          justifyContent: "center",
          padding: "0 24px",
        }}
      >
        {["Compiling", "Linking", "Serving"].map((label, i) => (
          <span
            key={label}
            style={{
              padding: "3px 10px",
              borderRadius: 99,
              fontSize: 11,
              fontWeight: 500,
              border: "1px solid",
              borderColor:
                i === stateIndex % 3 ? "rgba(99,102,241,0.4)" : "rgba(0,0,0,0.08)",
              color: i === stateIndex % 3 ? "#6366f1" : "#a1a1aa",
              background: i === stateIndex % 3 ? "rgba(99,102,241,0.06)" : "transparent",
              transition: "all 0.4s ease",
            }}
          >
            {i === stateIndex % 3 ? "● " : "○ "}
            {label}
          </span>
        ))}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes indeterminate {
          0%   { transform: translateX(-100%) scaleX(0.4); }
          50%  { transform: translateX(0%)    scaleX(0.6); }
          100% { transform: translateX(200%)  scaleX(0.4); }
        }
      `}</style>
    </div>
  );
}

